import { Injectable } from '@angular/core';
import {environment} from '../../../../environments/environment';
import {BehaviorSubject, catchError, filter, map, Observable, of, shareReplay, take, tap, throwError} from 'rxjs';
import {LoginRequest, MfaValidateRequest, TokenMfaResponse, TokenResponse} from '../../models/auth/auth.models';
import {LocalStorageService} from '../local-storage/local-storage.service';
import {HttpClient} from '@angular/common/http';
import {handleHttpError} from '../../helpers/http-error-response.helper';
import {ROLE_SUPER_ADMINISTRATOR, UserInfo} from '../../models/user/user-info.model';

@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {
  private readonly baseUrl = `${environment.backend.url}/api/auth/v1`;
  private readonly userInfoKey = 'FSBR-UserInfo';
  private readonly rolesUserInfoKey = 'FSBR-UserInfoRoles';
  private readonly accessTokenKey = 'FSBR-AccessToken';
  private readonly refreshTokenKey = 'FSBR-RefreshToken';
  private readonly tokenTypeKey = 'FSBR-TokenType';
  private readonly tokenExpiresKey = 'FSBR-TokenExpires';
  private readonly tokenRefreshExpectedTime = 'FSBR-TokenRefreshExpectedTime';
  private readonly refreshTokenExpiresKey = 'FSBR-RefreshTokenExpires';
  private readonly refreshTokenExpSecsKey = 'FSBR-RefreshTokenExpSecs';
  private readonly challengeTokenKey = 'FSBR-ChallengeToken';
  private readonly challengeTokenExpiresKey = 'FSBR-ChallengeTokenExpires';

  private readonly challengeTokenExpirationTime = 10 * 60;

  private refreshTokenInProgress = false;
  private refreshTokenSubject: BehaviorSubject<TokenResponse | null> = new BehaviorSubject<TokenResponse | null>(null);

  constructor(
    private readonly http: HttpClient,
    private readonly localStorageService: LocalStorageService
  ) { }

  /**
   * Logs in a user with the provided credentials
   * @param recaptchaToken
   * @param username The user's email
   * @param password The user's password
   * @param rememberMe Whether to remember the user
   * @returns An Observable with either TokenMfaResponse (if MFA is required) or TokenResponse (if login is successful)
   */
  public login(username: string, password: string, rememberMe: boolean = false): Observable<TokenMfaResponse | TokenResponse> {
    const loginRequest: LoginRequest = {
      username,
      password,
      rememberMe
    };

    return this.http.post<TokenMfaResponse | TokenResponse>(`${this.baseUrl}/token`, loginRequest, {
      observe: 'response'
    })
      .pipe(
        map(response => {
          const body = response.body as TokenMfaResponse | TokenResponse;

          // If status is 201, login was successful without MFA
          if (response.status === 201 && 'accessToken' in body) {
            this.handleSuccessfulLogin(body);
          } else if (response.status === 200 && 'challengeToken' in body) {
            // Store the challenge token with expiration time
            this.storeChallengeToken((body as TokenMfaResponse).challengeToken);
          }

          return body;
        }),
        catchError(handleHttpError)
      );
  }

  /**
   * Validates an MFA code
   * @param challengeToken The challenge token received from the login endpoint
   * @param mfaCode The MFA code entered by the user
   * @param recaptchaToken
   * @returns An Observable with TokenResponse
   */
  public validateMfa(challengeToken: string, mfaCode: string): Observable<TokenResponse> {
    // Check if the challenge token has expired
    if (this.isChallengeTokenExpired()) {
      return throwError(() => new Error('Challenge token has expired. Please login again.'));
    }

    const mfaRequest: MfaValidateRequest = {
      challengeToken,
      mfaCode
    };

    return this.http.post<TokenResponse>(`${this.baseUrl}/2fa/validate`, mfaRequest)
      .pipe(
        tap(response => {
          // Clear the challenge token after successful validation
          this.clearChallengeToken();
          this.handleSuccessfulLogin(response);
        }),
        catchError(error => {
          // If the error is due to an invalid challenge token, clear it
          if (error.status === 401 && error.error?.message?.includes('challenge token')) {
            this.clearChallengeToken();
          }
          return handleHttpError(error);
        })
      );
  }

  /**
   * Gets the user information
   * @returns An Observable with UserInfo
   */
  public getUserInfo(): Observable<UserInfo> {
    if (!this.isAuthenticated) {
      return throwError(() => new Error('User is not authenticated'));
    }

    // First check if we have the user info in local storage
    const cachedUserInfo = this.userData;
    if (cachedUserInfo) {
      return of(cachedUserInfo);
    }

    // If not, fetch it from the API
    return this.http.get<UserInfo>(`${this.baseUrl}/userinfo`)
      .pipe(
        tap(userInfo => {
          this.localStorageService.set(this.userInfoKey, userInfo);
        }),
        catchError(handleHttpError)
      );
  }

  /**
   * Logs out the user by removing all authentication data from local storage
   */
  public logout(): void {
    this.localStorageService.delete(this.userInfoKey);
    this.localStorageService.delete(this.accessTokenKey);
    this.localStorageService.delete(this.refreshTokenKey);
    this.localStorageService.delete(this.tokenTypeKey);
    this.localStorageService.delete(this.tokenExpiresKey);
    this.localStorageService.delete(this.tokenRefreshExpectedTime);
    this.localStorageService.delete(this.refreshTokenExpiresKey);
    this.localStorageService.delete(this.refreshTokenExpSecsKey);
    this.localStorageService.delete(this.challengeTokenKey);
    this.localStorageService.delete(this.challengeTokenExpiresKey);
    this.clearChallengeToken();
  }

  /**
   * Gets the user data from local storage
   */
  public get userData(): UserInfo | undefined {
    return this.localStorageService.get<UserInfo>(this.userInfoKey);
  }

  public get roles(): string[] {
    return this.localStorageService.get<string[]>(this.rolesUserInfoKey) || [];
  }

  public hasRole(role: string) {
    if (this.isSuperAdministrator()) {
      return true;
    }
    return this.roles.includes(role);
  }

  public isSuperAdministrator(): boolean {
    return this.roles.includes(ROLE_SUPER_ADMINISTRATOR);
  }

  public hasAnyRole(roles: string[]) {
    if (this.isSuperAdministrator()) {
      return true;
    }
    return roles.some(role => this.hasRole(role));
  }

  /**
   * Checks if the user is authenticated
   */
  public get isAuthenticated(): boolean {
    return !!this.accessToken || !!this.refreshToken;
  }

  /**
   * Gets the access token from local storage
   */
  public get accessToken(): string | undefined {
    return this.localStorageService.get<string>(this.accessTokenKey);
  }

  public get refreshToken(): string | undefined {
    return this.localStorageService.get<string>(this.refreshTokenKey);
  }

  /**
   * Gets the token type from local storage
   */
  public get tokenType(): string | undefined {
    return this.localStorageService.get<string>(this.tokenTypeKey);
  }

  /**
   * Gets the full authorization header value
   */
  public get authorizationHeader(): string | undefined {
    if (!this.accessToken || !this.tokenType || this.isTokenExpired()) {
      return undefined;
    }
    return `${this.tokenType} ${this.accessToken}`;
  }

  public isRefreshTokenTime(): boolean {
    const expiresAt = this.localStorageService.get<number>(this.tokenRefreshExpectedTime);
    if (!expiresAt) {
      return true;
    }
    return new Date().getTime() >= expiresAt;
  }

  /**
   * Checks if the token is expired
   */
  public isTokenExpired(): boolean {
    const expiresAt = this.localStorageService.get<number>(this.tokenExpiresKey);
    if (!expiresAt) {
      return true;
    }
    return new Date().getTime() > expiresAt;
  }

  public isRefreshTokenExpired(): boolean {
    const expiresAt = this.localStorageService.get<number>(this.refreshTokenExpiresKey);
    if (!expiresAt) {
      return true;
    }
    return new Date().getTime() > expiresAt;
  }

  /**
   * Handles a successful login by storing the token information
   */
  private handleSuccessfulLogin(response: TokenResponse): void {
    const expiresAt = new Date().getTime() + response.expiresIn * 1000;
    const expectedRefreshTime = new Date().getTime() + (response.expiresIn * 0.75) * 1000;

    this.localStorageService.set(this.accessTokenKey, response.accessToken, response.expiresIn);
    this.localStorageService.set(this.tokenTypeKey, response.tokenType, response.expiresIn);
    this.localStorageService.set(this.tokenExpiresKey, expiresAt, response.expiresIn);
    this.localStorageService.set(this.tokenRefreshExpectedTime, expectedRefreshTime, response.expiresIn);

    const accessTokenParts = this.accessToken?.split('.') || [''];
    const accessTokenPayload = JSON.parse(atob(accessTokenParts[1]));

    let roles: string[] = [];
    if (Array.isArray(accessTokenPayload.role)) {
      roles = accessTokenPayload.role;
    } else {
      roles.push(accessTokenPayload.role);
    }

    this.localStorageService.set(this.rolesUserInfoKey, roles, response.expiresIn);

    // decode the refresh token and get exp_secs for refresh expiration time
    const refreshToken = response.refreshToken;
    const refreshTokenParts = refreshToken.split('.');
    const refreshTokenPayload = JSON.parse(atob(refreshTokenParts[1]));
    const refreshTokenExpSecs = refreshTokenPayload.exp_secs;
    // const refreshExpiresAt = new Date().getTime() + refreshTokenPayload.exp * 1000;
    const refreshExpiresAt = refreshTokenPayload.exp * 1000;

    this.localStorageService.set(this.refreshTokenKey, refreshToken, refreshTokenExpSecs);
    this.localStorageService.set(this.refreshTokenExpiresKey, refreshExpiresAt, refreshTokenExpSecs);
    this.localStorageService.set(this.refreshTokenExpSecsKey, refreshTokenExpSecs, refreshTokenExpSecs);

    // Fetch and store user info
    this.getUserInfo().subscribe();
  }

  /**
   * Stores the challenge token with its expiration time
   * @param challengeToken The challenge token to store
   */
  private storeChallengeToken(challengeToken: string): void {
    const expiresAt = new Date().getTime() + this.challengeTokenExpirationTime * 1000;

    this.localStorageService.set(this.challengeTokenKey, challengeToken, this.challengeTokenExpirationTime);
    this.localStorageService.set(this.challengeTokenExpiresKey, expiresAt, this.challengeTokenExpirationTime);
  }

  /**
   * Gets the stored challenge token
   * @returns The challenge token or undefined if not found or expired
   */
  public getChallengeToken(): string | undefined {
    if (this.isChallengeTokenExpired()) {
      this.clearChallengeToken();
      return undefined;
    }
    return this.localStorageService.get<string>(this.challengeTokenKey);
  }

  /**
   * Checks if the challenge token has expired
   * @returns True if the token has expired or doesn't exist, false otherwise
   */
  public isChallengeTokenExpired(): boolean {
    const expiresAt = this.localStorageService.get<number>(this.challengeTokenExpiresKey);
    if (!expiresAt) {
      return true;
    }
    return new Date().getTime() > expiresAt;
  }

  /**
   * Clears the challenge token and its expiration time from storage
   */
  private clearChallengeToken(): void {
    this.localStorageService.delete(this.challengeTokenKey);
    this.localStorageService.delete(this.challengeTokenExpiresKey);
  }

  public doRefreshToken() {
    if (this.refreshTokenInProgress) {
      return this.refreshTokenSubject.pipe(
        filter(token => token !== null),
        take(1)
      );
    }

    this.refreshTokenInProgress = true;
    this.refreshTokenSubject.next(null);

    const refreshToken = this.refreshToken;
    return this.http.post<TokenResponse>(`${this.baseUrl}/refresh-token`, { refreshToken })
      .pipe(
        tap(response => {
          this.handleSuccessfulLogin(response);
          this.refreshTokenSubject.next(response);
          this.refreshTokenInProgress = false;
        }),
        catchError(error => {
          this.refreshTokenInProgress = false;
          this.refreshTokenSubject.next(null);
          if (error.status === 401) {
            this.logout();
          }
          return handleHttpError(error);
        }),
        shareReplay(1)
      );
  }

}
